/**
 * Pincode to State Validation for Indian Pincodes
 * Based on first 2 digits of pincode
 */

// Mapping of pincode starting digits to state codes
const pincodeStartToState = {
    "11": ["DL", "Delhi"],
    "12": ["HR", "Haryana"],
    "13": ["HR", "Haryana"],
    "14": ["PB", "Punjab"],
    "15": ["PB", "Punjab"],
    "16": ["PB", "Punjab"],
    "17": ["HP", "Himachal Pradesh"],
    "18": ["JK", "Jammu and Kashmir"],
    "19": ["JK", "Jammu and Kashmir"],
    "20": ["UP", "Uttar Pradesh"],
    "21": ["UP", "Uttar Pradesh"],
    "22": ["UP", "Uttar Pradesh"],
    "23": ["UP", "Uttar Pradesh"],
    "24": ["UT", "Uttarakhand"],
    "25": ["UP", "Uttar Pradesh"],
    "26": ["UT", "Uttarakhand"],
    "27": ["UP", "Uttar Pradesh"],
    "28": ["UP", "Uttar Pradesh"],
    "30": ["RJ", "Rajasthan"],
    "31": ["RJ", "Rajasthan"],
    "32": ["RJ", "Rajasthan"],
    "33": ["RJ", "Rajasthan"],
    "34": ["RJ", "Rajasthan"],
    "36": ["GJ", "Gujarat"],
    "37": ["GJ", "Gujarat"],
    "38": ["GJ", "Gujarat"],
    "39": ["GJ", "Gujarat"],
    "40": ["MH", "Maharashtra"],
    "41": ["MH", "Maharashtra"],
    "42": ["MH", "Maharashtra"],
    "43": ["MH", "Maharashtra"],
    "44": ["MH", "Maharashtra"],
    "45": ["MP", "Madhya Pradesh"],
    "46": ["MP", "Madhya Pradesh"],
    "47": ["MP", "Madhya Pradesh"],
    "48": ["MP", "Madhya Pradesh"],
    "49": ["CT", "Chhattisgarh"],
    "50": ["TG", "Telangana"],
    "51": ["AP", "Andhra Pradesh"],
    "52": ["AP", "Andhra Pradesh"],
    "53": ["AP", "Andhra Pradesh"],
    "56": ["KA", "Karnataka"],
    "57": ["KA", "Karnataka"],
    "58": ["KA", "Karnataka"],
    "59": ["KA", "Karnataka"],
    "60": ["TN", "Tamil Nadu"],
    "61": ["TN", "Tamil Nadu"],
    "62": ["TN", "Tamil Nadu"],
    "63": ["TN", "Tamil Nadu"],
    "64": ["TN", "Tamil Nadu"],
    "67": ["KL", "Kerala"],
    "68": ["KL", "Kerala"],
    "69": ["KL", "Kerala"],
    "70": ["WB", "West Bengal"],
    "71": ["WB", "West Bengal"],
    "72": ["WB", "West Bengal"],
    "73": ["WB", "West Bengal"],
    "74": ["WB", "West Bengal"],
    "75": ["OR", "Odisha"],
    "76": ["OR", "Odisha"],
    "77": ["OR", "Odisha"],
    "78": ["AS", "Assam"],
    "79": ["NL", "Nagaland"],
    "80": ["BR", "Bihar"],
    "81": ["BR", "Bihar"],
    "82": ["BR", "Bihar"],
    "83": ["JH", "Jharkhand"],
    "84": ["BR", "Bihar"],
    "85": ["BR", "Bihar"]
};

/**
 * Get state code from pincode based on first 2 digits
 * @param {string|number} pincode - 6 digit Indian pincode
 * @returns {string|null} - State code (e.g., 'DL', 'MH') or null if not found
 */
export const getStateFromPincode = (pincode) => {
    if (!pincode) return null;

    const pincodeStr = pincode.toString().trim();

    // Validate pincode format (should be 6 digits)
    if (pincodeStr.length !== 6 || isNaN(pincodeStr)) {
        return null;
    }

    // Get first 2 digits
    const firstTwoDigits = pincodeStr.substring(0, 2);

    // Find matching state
    const stateInfo = pincodeStartToState[firstTwoDigits];

    return stateInfo ? stateInfo[0] : null;
};

/**
 * Validate if pincode belongs to the selected state
 * @param {string|number} pincode - 6 digit Indian pincode
 * @param {string} stateCode - State code (e.g., 'DL', 'MH')
 * @returns {object} - { isValid: boolean, message: string }
 */
export const validateStatePincode = (pincode, stateCode) => {
    // If either is empty, skip validation
    if (!pincode || !stateCode) {
        return {
            isValid: true,
            message: ''
        };
    }

    const pincodeStr = pincode.toString().trim();

    // Check if pincode is 6 digits
    if (pincodeStr.length !== 6 || isNaN(pincodeStr)) {
        return {
            isValid: false,
            message: 'Pincode must be 6 digits'
        };
    }

    const detectedStateCode = getStateFromPincode(pincode);

    if (!detectedStateCode) {
        return {
            isValid: false,
            message: 'Invalid pincode or pincode not recognized'
        };
    }

    if (detectedStateCode !== stateCode) {
        const firstTwoDigits = pincodeStr.substring(0, 2);
        const stateInfo = pincodeStartToState[firstTwoDigits];
        const expectedStateName = stateInfo ? stateInfo[1] : detectedStateCode;

        return {
            isValid: false,
            message: `Pincode ${pincode} belongs to ${expectedStateName}, but you selected a different state`
        };
    }

    return {
        isValid: true,
        message: ''
    };
};

export default {
    getStateFromPincode,
    validateStatePincode
};
